import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import {
  getAllFAQs,
  getFAQById,
  updateFAQ,
  deleteFAQ,
  createFAQ,
} from "../../Utils/apiServices";
import Button from "../Button";
import Card from "../Card";
import TextField from "../TextField";
import { Typography } from "../Typography";
import toast from "../../Utils/toastManager";

const FAQList = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    is_active: true,
    display_order: "",
  });

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await getAllFAQs();
      if (response.success && response?.data?.data) {
        setFaqs(
          Array.isArray(response.data?.data)
            ? response.data?.data
            : []
        );
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEdit = async (faq) => {
    try {
      // Fetch full FAQ details if needed
      const response = await getFAQById(faq.id);
      if (response.success && response.data?.data) {
        const faqData = response.data.data;
        setEditingId(faq.id);
        setShowCreateForm(false);
        setFormData({
          title: faqData.title || "",
          description: faqData.description || "",
          is_active: faqData.is_active !== undefined ? faqData.is_active : true,
          display_order: faqData.display_order !== undefined ? faqData.display_order : "",
        });
      } else {
        // Fallback to the FAQ data we already have
        setEditingId(faq.id);
        setShowCreateForm(false);
        setFormData({
          title: faq.title || "",
          description: faq.description || "",
          is_active: faq.is_active !== undefined ? faq.is_active : true,
          display_order: faq.display_order !== undefined ? faq.display_order : "",
        });
      }
    } catch (error) {
      console.error("Error fetching FAQ details:", error);
      // Fallback to the FAQ data we already have
      setEditingId(faq.id);
      setShowCreateForm(false);
      setFormData({
        title: faq.title || "",
        description: faq.description || "",
        is_active: faq.is_active !== undefined ? faq.is_active : true,
        display_order: faq.display_order !== undefined ? faq.display_order : "",
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setShowCreateForm(false);
    setFormData({
      title: "",
      description: "",
      is_active: true,
      display_order: "",
    });
  };

  const handleSave = async (faqId = null) => {
    try {
      const dataToSave = {
        title: formData.title,
        description: formData.description,
        is_active: formData.is_active,
        display_order: formData.display_order ? parseInt(formData.display_order, 10) : null,
      };

      let response;
      if (faqId) {
        // Update existing FAQ
        response = await updateFAQ(faqId, dataToSave);
      } else {
        // Create new FAQ
        response = await createFAQ(dataToSave);
      }

      if (response.success) {
        await fetchFAQs();
        handleCancelEdit();
      } else {
        toast.error(
          `Failed to ${faqId ? "update" : "create"} FAQ: ${
            response.error || "Unknown error"
          }`
        );
      }
    } catch (error) {
      console.error(`Error saving FAQ:`, error);
      toast.error(`An error occurred while ${faqId ? "updating" : "creating"} the FAQ`);
    }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) {
      return;
    }

    try {
      const response = await deleteFAQ(faqId);
      if (response.success) {
        await fetchFAQs();
      } else {
        toast.error("Failed to delete FAQ: " + (response.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      toast.error("An error occurred while deleting the FAQ");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Filter FAQs based on search term
  const filteredFaqs = useMemo(() => {
    if (!searchTerm.trim()) return faqs;
    
    const searchLower = searchTerm.toLowerCase();
    return faqs.filter((faq) => {
      const title = (faq.title || "").toLowerCase();
      const description = (faq.description || "").toLowerCase();
      
      return (
        title.includes(searchLower) ||
        description.includes(searchLower)
      );
    });
  }, [faqs, searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Typography as="p" className="text-tertiary-600">
          Loading FAQs...
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <Typography as="h2" className="text-2xl font-semibold text-primary-900">
          FAQs
        </Typography>
        <Button
          onClick={() => {
            setShowCreateForm(true);
            setEditingId(null);
            setFormData({
              title: "",
              description: "",
              is_active: true,
              display_order: "",
            });
          }}
          startIcon={<Icon icon="lucide:plus" className="h-4 w-4" />}
        >
          Create FAQ
        </Button>
      </div>

      {showCreateForm && (
        <Card className="border border-slate-200 p-6">
          <div className="space-y-4">
            <Typography
              as="h3"
              className="text-base font-semibold text-primary-900 mb-4"
            >
              Create New FAQ
            </Typography>
            <div className="grid grid-cols-1 gap-4">
              <TextField
                name="title"
                label="Title / Question"
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                required
                placeholder="Enter FAQ title or question"
              />
              <div>
                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                  Description / Answer
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Enter detailed answer or description"
                  rows={6}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <TextField
                name="display_order"
                label="Display Order"
                type="number"
                value={formData.display_order}
                onChange={(e) => handleInputChange("display_order", e.target.value)}
                placeholder="Enter display order (e.g., 1, 2, 3...)"
                min="1"
              />
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange("is_active", e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-primary-900">Active</span>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="secondary" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button onClick={() => handleSave()}>Create FAQ</Button>
            </div>
          </div>
        </Card>
      )}

      <Card 
        className="border border-slate-200 overflow-hidden"
        header={
          <div className="flex items-center justify-end w-full">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-48 px-3 py-1.5 pl-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
              />
              <Icon
                icon="lucide:search"
                className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400"
              />
            </div>
          </div>
        }
      >
        {faqs.length === 0 ? (
          <div className="p-6 text-center">
            <Typography as="p" className="text-tertiary-600">
              No FAQs found.
            </Typography>
          </div>
        ) : (
          <div className="overflow-x-auto">
              <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Title / Question
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Description / Answer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Display Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredFaqs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center">
                      <Typography as="p" className="text-tertiary-600">
                        No FAQs found matching your search.
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  filteredFaqs.map((faq) => (
                  <React.Fragment key={faq.id}>
                    {editingId === faq.id ? (
                      <tr className="bg-slate-50">
                        <td colSpan="6" className="px-6 py-4">
                          <div className="space-y-4">
                            <Typography
                              as="h3"
                              className="text-base font-semibold text-primary-900 mb-4"
                            >
                              Edit FAQ
                            </Typography>
                            <div className="grid grid-cols-1 gap-4">
                              <TextField
                                name="title"
                                label="Title / Question"
                                type="text"
                                value={formData.title}
                                onChange={(e) =>
                                  handleInputChange("title", e.target.value)
                                }
                                required
                                placeholder="Enter FAQ title or question"
                              />
                              <div>
                                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                  Description / Answer
                                </label>
                                <textarea
                                  name="description"
                                  value={formData.description}
                                  onChange={(e) =>
                                    handleInputChange("description", e.target.value)
                                  }
                                  placeholder="Enter detailed answer or description"
                                  rows={6}
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              </div>
                              <TextField
                                name="display_order"
                                label="Display Order"
                                type="number"
                                value={formData.display_order}
                                onChange={(e) => handleInputChange("display_order", e.target.value)}
                                placeholder="Enter display order (e.g., 1, 2, 3...)"
                                min="1"
                              />
                              <div>
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => handleInputChange("is_active", e.target.checked)}
                                    className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                                  />
                                  <span className="text-sm font-medium text-primary-900">Active</span>
                                </label>
                              </div>
                            </div>
                            <div className="flex items-center justify-end gap-3">
                              <Button variant="secondary" onClick={handleCancelEdit}>
                                Cancel
                              </Button>
                              <Button onClick={() => handleSave(faq.id)}>
                                Save
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <Typography
                            as="span"
                            className="text-sm font-medium text-primary-900 line-clamp-1"
                            title={faq.title}
                          >
                            {faq.title || "-"}
                          </Typography>
                        </td>
                        <td className="px-6 py-4">
                          <Typography
                            as="span"
                            className="text-sm text-tertiary-600 line-clamp-2"
                            title={faq.description}
                          >
                            {faq.description
                              ? faq.description.substring(0, 150)
                              : "-"}
                            {faq.description && faq.description.length > 150 && "..."}
                          </Typography>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              faq.is_active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {faq.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Typography
                            as="span"
                            className="text-sm text-primary-900 font-medium"
                          >
                            {faq.display_order !== undefined && faq.display_order !== null
                              ? faq.display_order
                              : "-"}
                          </Typography>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Typography
                            as="span"
                            className="text-sm text-primary-900"
                          >
                            {formatDate(faq.created_at)}
                          </Typography>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleEdit(faq)}
                              startIcon={<Icon icon="lucide:edit" className="h-4 w-4" />}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleDelete(faq.id)}
                              startIcon={
                                <Icon icon="lucide:trash-2" className="h-4 w-4" />
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default FAQList;

